/**
 * CompetitionRegisterPage — запись ребёнка на соревнование + оплата.
 * Маршрут: /cabinet/competitions/:id
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Stack, Box, Text, Title, Paper, Button, Loader, Center,
  Select, TextInput, Badge, Group, Divider,
} from '@mantine/core';
import {
  IconArrowLeft, IconTrophy, IconCreditCard, IconUserPlus, IconCheck,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import CabinetLayout from '../components/CabinetLayout';
import { cabinetService } from '../services/api';
import tokens from '../theme/designTokens';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Moscow',
  });
};

const formatRub = (val) =>
  Number(val).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';

const CompetitionRegisterPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Форма записи
  const [mode, setMode] = useState('existing'); // existing | new
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');

  useEffect(() => {
    let mounted = true;
    cabinetService.getActiveCompetitions()
      .then((data) => mounted && setCompetitions(data))
      .catch((e) => mounted && notifications.show({ title: 'Ошибка', message: e.message, color: 'red' }))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const comp = competitions.find((c) => c.id === id);

  const handleRegister = async () => {
    setSubmitting(true);
    try {
      if (mode === 'existing') {
        if (!selectedStudentId) {
          throw new Error('Выберите ребёнка');
        }
        await cabinetService.registerForCompetition(id, {
          student_id: Number(selectedStudentId),
          subject_id: selectedSubjectId ? Number(selectedSubjectId) : null,
        });
      } else {
        if (!newFirstName.trim()) {
          throw new Error('Введите имя ребёнка');
        }
        await cabinetService.registerNewForCompetition(id, {
          first_name: newFirstName.trim(),
          last_name: newLastName.trim() || null,
          subject_id: selectedSubjectId ? Number(selectedSubjectId) : null,
        });
      }
      notifications.show({
        title: 'Записано!',
        message: 'Ребёнок записан на соревнование',
        color: 'green',
        icon: <IconCheck size={18} />,
      });
      // Перезагружаем данные чтобы обновить состояние
      const data = await cabinetService.getActiveCompetitions();
      setCompetitions(data);
      setSelectedStudentId(null);
      setNewFirstName('');
      setNewLastName('');
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <CabinetLayout>
        <Center py={80}><Loader color="brand-purple" /></Center>
      </CabinetLayout>
    );
  }

  if (!comp) {
    return (
      <CabinetLayout>
        <Center py={80}>
          <Text c={tokens.colors.text.muted} fw={700}>Соревнование не найдено</Text>
        </Center>
      </CabinetLayout>
    );
  }

  const subjectOptions = (comp.subjects || []).map((s) => ({
    value: String(s.id),
    label: s.name,
  }));

  const studentOptions = (comp.available_students || []).map((s) => ({
    value: String(s.id),
    label: s.name,
  }));

  const hasAvailable = studentOptions.length > 0;
  const hasPending = comp.my_participants.some((p) => p.payment_status === 'pending');
  const allRegisteredAndPaid = comp.my_participants.length > 0 &&
    comp.my_participants.every((p) => p.payment_status === 'paid') &&
    comp.available_students.length === 0;

  return (
    <CabinetLayout>
      <Stack gap="lg">
        <Button
          variant="subtle"
          color="brand-purple"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/')}
          style={{ alignSelf: 'flex-start' }}
        >
          Назад
        </Button>

        {/* Заголовок */}
        <Paper p="xl" radius="xl" style={{ background: 'white' }}>
          <Group gap="md" align="flex-start">
            <Box
              style={{
                width: 56,
                height: 56,
                borderRadius: 18,
                background: 'linear-gradient(135deg, #FFF7ED, #FFE8CC)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tokens.colors.brand.orange,
                flexShrink: 0,
              }}
            >
              <IconTrophy size={28} stroke={2.5} />
            </Box>
            <Box>
              <Title
                order={2}
                style={{
                  fontFamily: tokens.typography.fontFamilyHeading,
                  color: tokens.colors.text.primary,
                }}
              >
                {comp.name}
              </Title>
              <Text c={tokens.colors.text.muted} fw={700} mt={4}>
                {formatDate(comp.event_date)}
              </Text>
              {comp.description && (
                <Text size="sm" c={tokens.colors.text.muted} fw={700} mt={8}>
                  {comp.description}
                </Text>
              )}
              {comp.subjects.length > 0 && (
                <Group gap={4} mt={8}>
                  {comp.subjects.map((s) => (
                    <Badge key={s.id} size="sm" variant="filled" color="blue" radius="xl">
                      {s.name}
                    </Badge>
                  ))}
                </Group>
              )}
            </Box>
          </Group>
        </Paper>

        {/* Стоимость */}
        {comp.price && (
          <Paper
            p="md"
            radius="xl"
            style={{
              background: tokens.gradients.softPurple,
              border: '2px solid rgba(124,58,237,0.08)',
            }}
          >
            <Group justify="space-between" align="center">
              <Text fw={900} style={{ fontFamily: tokens.typography.fontFamilyHeading, color: tokens.colors.text.primary }}>
                Стоимость участия
              </Text>
              <Group gap={8} align="baseline">
                <Text
                  fw={1000}
                  style={{
                    fontFamily: tokens.typography.fontFamilyHeading,
                    fontSize: 24,
                    color: tokens.colors.brand.orange,
                  }}
                >
                  {formatRub(comp.price_with_discount)}
                </Text>
                {comp.discount_active && Number(comp.price) !== Number(comp.price_with_discount) && (
                  <Text fw={700} size="sm" style={{ color: tokens.colors.text.muted, textDecoration: 'line-through' }}>
                    {formatRub(comp.price)}
                  </Text>
                )}
                <Text size="sm" c={tokens.colors.text.muted} fw={700}>за ребёнка</Text>
              </Group>
            </Group>
          </Paper>
        )}

        {/* Уже записанные */}
        {comp.my_participants.length > 0 && (
          <Paper p="lg" radius="xl" style={{ background: 'white' }}>
            <Text fw={900} style={{ fontFamily: tokens.typography.fontFamilyHeading, color: tokens.colors.text.primary }} mb="sm">
              Записанные дети
            </Text>
            <Stack gap="xs">
              {comp.my_participants.map((p) => (
                <Group key={p.id} justify="space-between" align="center">
                  <Group gap="xs">
                    <Text fw={800}>{p.student_name}</Text>
                    {p.subject_name && (
                      <Badge size="xs" variant="filled" color="blue" radius="xl">{p.subject_name}</Badge>
                    )}
                  </Group>
                  <Badge
                    size="sm"
                    variant="filled"
                    color={p.payment_status === 'paid' ? 'green' : 'orange'}
                    radius="xl"
                  >
                    {p.payment_status === 'paid' ? 'Оплачено' : 'Ожидает оплаты'}
                  </Badge>
                </Group>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Форма записи */}
        {!allRegisteredAndPaid && (
          <Paper p="xl" radius="xl" style={{ background: 'white' }}>
            <Stack gap="md">
              <Title order={4} style={{ color: tokens.colors.text.primary }}>
                Записать ребёнка
              </Title>

              {hasAvailable && (
                <Box
                  onClick={() => setMode('existing')}
                  style={{
                    padding: 16,
                    borderRadius: 16,
                    background: mode === 'existing' ? tokens.gradients.softPurple : tokens.colors.bg.page,
                    border: `2px solid ${mode === 'existing' ? tokens.colors.brand.purple : 'transparent'}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <Text fw={800} style={{ color: tokens.colors.text.primary }} mb="xs">
                    Выбрать из привязанных
                  </Text>
                  <Select
                    placeholder="Ребёнок"
                    data={studentOptions}
                    value={selectedStudentId}
                    onChange={(v) => { setSelectedStudentId(v); setMode('existing'); }}
                    searchable
                    radius="xl"
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>
              )}

              <Box
                onClick={() => setMode('new')}
                style={{
                  padding: 16,
                  borderRadius: 16,
                  background: mode === 'new' ? tokens.gradients.softPurple : tokens.colors.bg.page,
                  border: `2px solid ${mode === 'new' ? tokens.colors.brand.purple : 'transparent'}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <Group gap="xs" mb="xs">
                  <IconUserPlus size={16} color={tokens.colors.brand.purple} />
                  <Text fw={800} style={{ color: tokens.colors.text.primary }}>
                    Записать нового ребёнка
                  </Text>
                </Group>
                <Group grow onClick={(e) => e.stopPropagation()}>
                  <TextInput
                    placeholder="Имя"
                    value={newFirstName}
                    onChange={(e) => { setNewFirstName(e.target.value); setMode('new'); }}
                    radius="xl"
                  />
                  <TextInput
                    placeholder="Фамилия (необяз.)"
                    value={newLastName}
                    onChange={(e) => { setNewLastName(e.target.value); setMode('new'); }}
                    radius="xl"
                  />
                </Group>
              </Box>

              {subjectOptions.length > 0 && (
                <Select
                  label="Предмет (необязательно)"
                  placeholder="Выберите предмет"
                  data={subjectOptions}
                  value={selectedSubjectId}
                  onChange={setSelectedSubjectId}
                  clearable
                  radius="xl"
                />
              )}

              <Button
                size="lg"
                color="brand-orange"
                leftSection={<IconCreditCard size={20} />}
                loading={submitting}
                onClick={handleRegister}
                fullWidth
              >
                Записать
              </Button>
            </Stack>
          </Paper>
        )}

        {/* TODO: после записи → кнопка оплаты (Этап 4) */}
        {hasPending && (
          <Text size="xs" c={tokens.colors.text.muted} fw={700} ta="center">
            Оплата участия будет доступна после подтверждения записи.
          </Text>
        )}
      </Stack>
    </CabinetLayout>
  );
};

export default CompetitionRegisterPage;
